import React from "react";
import DummyBarcode from "../../../../assets/dummy-barcode.png";
import PrintIcon from "../../../../assets/icons/PrintIcon";
import DownloadIcon from "../../../../assets/icons/DownloadIcon";
import "../../../../style/returnsRegister.css";

function PrintReturnRegister({ isOpen, onClose, returnData, deliveryInfo }) {
  if (!isOpen || !returnData) return null;

  // Format date for display (e.g., "Oct 15, 2025")
  const formatReturnDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const dateObj =
        typeof dateString === "string" ? new Date(dateString) : dateString;
      if (isNaN(dateObj.getTime())) return dateString || "-";

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[dateObj.getMonth()];
      const day = dateObj.getDate();
      const year = dateObj.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString || "-";
    }
  };

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Get delivery info
  const deliveryId = deliveryInfo?.delivery_id || "-";
  const vendorName = deliveryInfo?.vendor_name || "-";
  const totalQty = deliveryInfo?.total_qty || 0;
  const totalValue = deliveryInfo?.total_value || 0;
  const generatedDate = new Date().toLocaleDateString();

  // Get return ID
  const returnId = returnData.return_id || returnData.id || "-";

  // Get device information
  const device = returnData.model || returnData.product_name || "-";
  const deviceColor = returnData.color || "";
  const deviceStorage = returnData.storage || "";
  const deviceCondition = returnData.condition || "";

  // Format device info: "Model - Color Storage | Condition"
  let deviceInfo = device;
  if (deviceColor && deviceStorage) {
    deviceInfo = `${device} - ${deviceColor} ${deviceStorage}`;
  } else if (deviceColor) {
    deviceInfo = `${device} - ${deviceColor}`;
  } else if (deviceStorage) {
    deviceInfo = `${device} ${deviceStorage}`;
  }
  if (deviceCondition) {
    deviceInfo = `${deviceInfo} | ${deviceCondition}`;
  }

  // Get customer name
  const customer = returnData.customer || returnData.customer_name || "-";

  // Get defect note
  const defectNote =
    returnData.defect_note || returnData.defect || returnData.notes || "-";

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export functionality
    console.log("Export PDF functionality to be implemented");
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="print-return-modal-overlay" onClick={handleCancel}>
      <div className="print-return-modal" onClick={(e) => e.stopPropagation()}>
        <div className="print-return-modal-content">
          {/* Title */}
          <div 
            className="print-return-title"
            style={{
              textAlign: "center",
              marginBottom: "24px",
              paddingBottom: "16px",
              width: "100%"
            }}
          >
            <h2 style={{
              fontWeight: "700",
              fontSize: "22px",
              margin: 0,
              color: "#424a53",
              textAlign: "center"
            }}>
              Supplier Return Detail Report
            </h2>
          </div>

          {/* Delivery Info Line */}
          <div 
            className="print-return-delivery-info"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: "8px",
              marginBottom: "24px",
              fontSize: "13px",
              color: "#424a53",
              lineHeight: "1.5",
              padding: "12px",
              backgroundColor: "#f6f8fa",
              borderRadius: "6px",
              width: "100%",
              overflowX: "auto",
              whiteSpace: "nowrap"
            }}
          >
            <span style={{ whiteSpace: "nowrap", display: "inline-block", flexShrink: 0 }}>
              <strong>Delivery ID:</strong> {deliveryId}
            </span>
            <span className="separator" style={{ color: "#d1d5db", flexShrink: 0 }}>|</span>
            <span style={{ whiteSpace: "nowrap", display: "inline-block", flexShrink: 0 }}>
              <strong>Supplier:</strong> {vendorName}
            </span>
            <span className="separator" style={{ color: "#d1d5db", flexShrink: 0 }}>|</span>
            <span style={{ whiteSpace: "nowrap", display: "inline-block", flexShrink: 0 }}>
              <strong>TOTAL QUANTITY:</strong> {formatNumber(totalQty)}
            </span>
            <span className="separator" style={{ color: "#d1d5db", flexShrink: 0 }}>|</span>
            <span style={{ whiteSpace: "nowrap", display: "inline-block", flexShrink: 0 }}>
              <strong>TOTAL VALUE:</strong> {formatCurrency(totalValue)}
            </span>
            <span className="separator" style={{ color: "#d1d5db", flexShrink: 0 }}>|</span>
            <span style={{ whiteSpace: "nowrap", display: "inline-block", flexShrink: 0 }}>
              <strong>Generated on:</strong> {generatedDate}
            </span>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-modal-header">
            <div className="print-return-header-left">
              <div className="print-return-label">Return ID</div>
              <div className="print-return-value">{returnId}</div>
            </div>
            <div className="print-return-header-center">
              <img
                src={DummyBarcode}
                alt="Barcode"
                className="print-return-barcode"
              />
            </div>
            <div className="print-return-header-right">
              <div
                className="print-return-label"
                style={{ paddingLeft: "3.8rem" }}
              >
                Date
              </div>
              <div className="print-return-value">
                {formatReturnDate(
                  returnData.date ||
                    returnData.created_at ||
                    returnData.return_date
                )}
              </div>
            </div>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-section">
            <div className="print-return-label">Device</div>
            <div className="print-return-value">{deviceInfo}</div>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-section">
            <div className="print-return-label">Customer:</div>
            <div className="print-return-value">{customer}</div>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-section">
            <div className="print-return-label">Defect Note</div>
            <div className="print-return-value">{defectNote}</div>
          </div>
        </div>

        <div className="print-return-modal-actions">
          <button className="print-return-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="print-return-export-btn" onClick={handleExportPDF}>
            <DownloadIcon />
            Export PDF
          </button>
          <button className="print-return-print-btn" onClick={handlePrint}>
            <PrintIcon />
            Print
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrintReturnRegister;
